// /home/coder/project/frontend/src/pages/qaqc/inspections/QaqcInspFormDesign.tsx

import React, { useState, useMemo, Key, useEffect } from 'react'; // Import Key type
import { Form, Input, Spin, Row, Col, message, Card, Transfer, Select } from 'antd'; // Import Select
import type { ValidateErrorEntity } from 'rc-field-form/lib/interface'; // For onFinishFailed typing
import type { TransferDirection } from 'antd/es/transfer'; // Import the type for direction
import { useGetQaqcInspFormData } from '../../../hooks/genericHooks'; // Hook for fetching QA/QC checklist items
import { useQueryClient } from '@tanstack/react-query'; // Import useQueryClient
import { QaqcInspFormData, QaqcInspFormDesigns } from '../../../types'; // QA/QC types
import { useNavigate } from 'react-router-dom';
import { insertData } from '../../../services/api'; // Import insertData directly
import { Typography } from 'antd'; // Import Typography
import Button from '../../../components/common/Button';

// Define the structure for items used in the Transfer component
interface TransferItem {
  key: string; 
  title: string;
  description: string; 
  disabled: boolean; 
  category: string;
  check_item: string;
  discipline: string; 
  inspform_number: string; 
}

const { Option } = Select; // Destructure Option from Select
const { Title } = Typography; // Destructure Title

const QaqcInspFormDesign: React.FC = () => {
  const [form] = Form.useForm();

  const navigate = useNavigate();
  const queryClient = useQueryClient(); // Get query client instance
  // State for Transfer component: targetKeys holds the keys (IDs) of items moved to the right panel
  const [targetKeys, setTargetKeys] = useState<Key[]>([]); // Use Key[] type for state
  // State to manage loading during submission

  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<string | null>(null);
  const [selectedDisciplineFilter, setSelectedDisciplineFilter] = useState<string | null>(null);

  const [searchCheckItemText, setSearchCheckItemText] = useState<string>(''); // Renamed for clarity, targets check_item
  const [isCreating, setIsCreating] = useState(false);

  // --- Data Fetching ---
  // Fetch QA/QC checklist items using the hook
  const {
    data: checklistDataResult, 
    isLoading: isLoadingItems,
    error: errorLoadingItems,
  } = useGetQaqcInspFormData(); // Changed hook

  // --- Data Preparation for Transfer ---
  // Extract the actual array of items from the nested structure
  const allChecklistItems = useMemo(() => checklistDataResult?.data || [], [checklistDataResult]);

  // Get unique categories for the filter dropdown
  const categories = useMemo(() => {
    return Array.from(new Set(allChecklistItems.map(item => item.category).filter(Boolean))).sort();
  }, [allChecklistItems]);

  // Get unique disciplines for the filter dropdown
  const disciplines = useMemo(() => {
    return Array.from(new Set(allChecklistItems.map(item => item.discipline).filter(Boolean))).sort();
  }, [allChecklistItems]);

  // Prepare dataSource for Transfer component
  const transferDataSource = useMemo((): TransferItem[] => {
    const allItemsMapped = allChecklistItems
      .filter(item => item.id != null && item.check_item != null && item.category != null && item.discipline != null) // inspform_number no longer a strict requirement for display
      .map(item => ({
        key: item.id.toString(),
        title: `${item.check_item} (${item.category} / ${item.discipline})`, // Simplified title
        description: `${item.category} - ${item.discipline}`,
        disabled: false,
        category: item.category,
        check_item: item.check_item,
        discipline: item.discipline,
        inspform_number: item.inspform_number,
      }));

    let availableItems = allItemsMapped;
    if (selectedDisciplineFilter) {
      availableItems = availableItems.filter(item => item.discipline === selectedDisciplineFilter);
    }
    if (selectedCategoryFilter) {
      availableItems = availableItems.filter(item => item.category === selectedCategoryFilter);
    }
    if (searchCheckItemText) {
      // Filter specifically by check_item text
      availableItems = availableItems.filter(item => item.check_item.toLowerCase().includes(searchCheckItemText.toLowerCase()));
    }

    const selectedItemsMap = new Map<Key, TransferItem>();
    const targetKeySet = new Set(targetKeys);
    allItemsMapped.forEach(item => {
        if (targetKeySet.has(item.key)) {
            selectedItemsMap.set(item.key, item);
        }
    });

    const finalDataSourceMap = new Map<Key, TransferItem>(selectedItemsMap);
    availableItems.forEach(item => {
      if (!finalDataSourceMap.has(item.key)) {
        finalDataSourceMap.set(item.key, item);
      }
    });

    return Array.from(finalDataSourceMap.values());

  }, [allChecklistItems, selectedCategoryFilter, selectedDisciplineFilter, searchCheckItemText, targetKeys]);

  // --- Form Submission ---
  interface FormValues {
    form_num: string;
    form_name: string;
  }

  const onFinish = async (values: FormValues) => {
    if (targetKeys.length === 0) {
        message.error('Please select at least one check item for the form design.');
        return;
    }

    const payload: Partial<QaqcInspFormDesigns> = { // Changed type
      form_num: values.form_num,
      form_name: values.form_name,
      form_fields: targetKeys.map(key => parseInt(String(key), 10)),
    };

    console.log('!!! PAYLOAD to be sent to insertData:', JSON.stringify(payload, null, 2));
    setIsCreating(true);
    try {
      const response = await insertData('qaqc_insp_form_designs', payload); // Changed table name
      console.log('Success response:', response);
      message.success('QA/QC Form design created successfully!');
      form.resetFields();
      setTargetKeys([]);
      queryClient.invalidateQueries({ queryKey: ['vw_qaqc_insp_formdetails'] }); // Changed query key
      navigate('/qaqc/inspections/form_list'); // Adjusted navigation path
    } catch (error) {
      console.error('Error creating QA/QC form design:', error);
      message.error(`Failed to create QA/QC form design: ${error instanceof Error ? error.message : 'An unknown error occurred'}`);
    } finally {
      setIsCreating(false);
    }
  };

  const onFinishFailed = (errorInfo: ValidateErrorEntity<FormValues>) => {
    console.log('QAQC Form validation FAILED (form.getFieldsValue()):', form.getFieldsValue()); // Direct check
    message.error('Form submission failed. Please check the fields for errors.');
    errorInfo.errorFields.forEach((fieldError) => {
      // error.name is an array of path segments (e.g., ['user', 'name'])
      // error.errors is an array of error messages for that field
      console.error(`Field: ${fieldError.name.join('.')}, Errors: ${fieldError.errors.join(', ')}`);
      message.warning(`Error in field "${fieldError.name.join('.')}": ${fieldError.errors.join(', ')}`);
    });
  };

  const handleTransferChange = (
    newTargetKeys: Key[],
    direction: TransferDirection,
    moveKeys: Key[]) => {
    setTargetKeys(newTargetKeys);
  };

  if (isLoadingItems) {
    // Wrap the Spin in the Card structure and provide a placeholder child for the "nest" pattern.
    return (
      <Card title="New QA/QC Inspection Form Design">
        <Spin tip="Loading QA/QC checklist items...">
          <div style={{ minHeight: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }} /> {/* Placeholder for form content */}
        </Spin>
      </Card>
    );
  }

  if (errorLoadingItems) {
    return <div style={{ color: 'red' }}>Error loading QA/QC checklist items: {errorLoadingItems.message}</div>;
  }

  return (
    <Card 
      title={
        <Title level={4} style={{ margin: 0 }}>New QA/QC Inspection Form Design</Title>
      }
    >
      <Spin spinning={isCreating} tip="Creating form design...">
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          onFinishFailed={onFinishFailed} // Add the onFinishFailed handler
          // initialValues prop removed; values are set via useEffect
          autoComplete="off"
        >
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Form Number"
                name="form_num"
                rules={[{ required: true, message: 'Please input the form number!' }]}
              >
                <div> {/* Explicit single child wrapper for diagnostics */}
                  <Input placeholder="e.g., QAQC-CIV-001" />
                </div>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Form Name"
                name="form_name"
                rules={[{ required: true, message: 'Please input the form name!' }]}
              >
                <div> {/* Explicit single child wrapper for diagnostics */}
                  <Input placeholder="e.g., Concrete Pour Checklist" />
                </div>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            label="Select Check Items"
            validateStatus={targetKeys.length === 0 ? 'error' : ''}
            help={targetKeys.length === 0 ? 'Please select at least one check item!' : ''}
          >
            <div> {/* Wrap complex content in a single div */}
              <Row gutter={8} style={{ marginBottom: '10px' }}>
                <Col xs={24} sm={8}>
                  {/* Discipline Filter Dropdown */}
                  <Select
                    placeholder="Filter by Discipline"
                    allowClear
                    style={{ width: '100%'}}
                    onChange={(value) => setSelectedDisciplineFilter(value)}
                    showSearch
                    filterOption={(input, option) => (option?.children as unknown as string ?? '').toLowerCase().includes(input.toLowerCase())}
                    value={selectedDisciplineFilter}
                  >
                    {disciplines.map(discipline => (
                      <Option key={discipline} value={discipline}>
                        {discipline}
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col xs={24} sm={8}>
                  {/* Category Filter Dropdown */}
                  <Select
                    placeholder="Filter by Category"
                    allowClear
                    style={{ width: '100%'}}
                    onChange={(value) => setSelectedCategoryFilter(value)}
                    showSearch // Enable search functionality
                    filterOption={(input, option) => (option?.children as unknown as string ?? '').toLowerCase().includes(input.toLowerCase())} // Custom filter logic
                    value={selectedCategoryFilter}
                  >
                    {categories.map(category => (
                      <Option key={category} value={category}>
                        {category}
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col xs={24} sm={8}>
                  {/* Check Item Search Input */}
                  <Input.Search
                    placeholder="Search check items"
                    allowClear
                    onChange={(e) => setSearchCheckItemText(e.target.value)}
                    style={{ width: '100%' }}
                  />
                </Col>
              </Row>
              <Transfer<TransferItem>
                dataSource={transferDataSource}
                showSearch={false}
                targetKeys={targetKeys}
                onChange={handleTransferChange}
                render={item => item.title}
                listStyle={{
                  width: '48%',
                  height: 400,
                }}
                titles={['Available Items', 'Selected Items']}
                rowKey={record => record.key}
              />
            </div>
          </Form.Item>

          <Form.Item wrapperCol={{ xs: { span: 24 }, sm: { span: 16, offset: 8 } }} style={{ textAlign: 'right' }}>
            <Button 
              action="add"
              type="primary" 
              htmlType="submit"
              loading={isCreating}>
              Create Form Design
            </Button>
            <Button style={{ marginLeft: 8 }} onClick={() => navigate(-1)}>
              Cancel
            </Button>
          </Form.Item>
        </Form>
      </Spin>
    </Card>
  );
};

export default QaqcInspFormDesign;
